/**
 * Pagina de Gestion de Plugins
 * Permite instalar, activar, desactivar, desinstalar y descargar plugins
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { pluginsAPI } from '../utils/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  Package,
  Upload,
  Search,
  Power,
  PowerOff,
  Trash2,
  Download,
  Loader2,
  Info,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileCode,
  Settings,
  Shield,
  ChevronDown,
  ChevronUp,
  Eye,
  HelpCircle,
  BookOpen,
  Code2,
  Puzzle,
  Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const STATUS_CONFIG = {
  active: { label: 'Activo', color: 'border-green-300 text-green-700 bg-green-50', icon: CheckCircle2 },
  installed: { label: 'Instalado', color: 'border-blue-300 text-blue-700 bg-blue-50', icon: Package },
  inactive: { label: 'Inactivo', color: 'border-gray-300 text-gray-500 bg-gray-50', icon: XCircle },
  error: { label: 'Error', color: 'border-red-300 text-red-700 bg-red-50', icon: AlertTriangle },
};

const Plugins = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPlugin, setExpandedPlugin] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plugin: null });
  const [downloadingDemo, setDownloadingDemo] = useState(false);
  const [docDialog, setDocDialog] = useState({ open: false, title: '', content: '', loading: false });

  useEffect(() => { loadData(); }, []);

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await pluginsAPI.getAll();
      setPlugins(response.data || []);
    } catch (error) {
      toast.error('Error al cargar plugins');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const pluginsFiltrados = useMemo(() => {
    if (!searchTerm) return plugins;
    const term = searchTerm.toLowerCase();
    return plugins.filter(p =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.description || '').toLowerCase().includes(term) ||
      (p.plugin_id || '').toLowerCase().includes(term)
    );
  }, [plugins, searchTerm]);

  const handleInstall = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      toast.error('El archivo debe ser un .zip');
      return;
    }
    setInstalling(true);
    try {
      const response = await pluginsAPI.install(file);
      const data = response.data;
      if (data.success) {
        toast.success(`Plugin "${data.name}" v${data.version} instalado correctamente`);
        if (data.warnings?.length) {
          data.warnings.forEach(w => toast.warning(w));
        }
        loadData(true);
      } else {
        toast.error(data.error || 'Error al instalar plugin');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al instalar plugin');
    } finally {
      setInstalling(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleActivate = async (plugin) => {
    setActionLoading(plugin.plugin_id + '_activate');
    try {
      await pluginsAPI.activate(plugin.plugin_id);
      toast.success(`Plugin "${plugin.name}" activado`);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (plugin) => {
    setActionLoading(plugin.plugin_id + '_deactivate');
    try {
      await pluginsAPI.deactivate(plugin.plugin_id);
      toast.success(`Plugin "${plugin.name}" desactivado`);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUninstall = async () => {
    const plugin = deleteDialog.plugin;
    if (!plugin) return;
    setActionLoading(plugin.plugin_id + '_uninstall');
    try {
      await pluginsAPI.uninstall(plugin.plugin_id);
      toast.success(`Plugin "${plugin.name}" desinstalado`);
      setDeleteDialog({ open: false, plugin: null });
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desinstalar');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async (plugin) => {
    setActionLoading(plugin.plugin_id + '_download');
    try {
      const response = await pluginsAPI.download(plugin.plugin_id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plugin.plugin_id}-v${plugin.version}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga iniciada');
    } catch (error) {
      toast.error('Error al descargar plugin');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadDemo = async () => {
    setDownloadingDemo(true);
    try {
      const response = await pluginsAPI.downloadDemo();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plugin-demo.zip';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Plugin Demo descargado');
    } catch (error) {
      toast.error('Plugin Demo no disponible');
    } finally {
      setDownloadingDemo(false);
    }
  };

  const isActionLoading = (pluginId, action) => actionLoading === `${pluginId}_${action}`;

  const HELP_DOCS = [
    { file: 'MANUAL_USUARIO.md', label: 'Manual de Usuario', icon: BookOpen },
    { file: 'DESARROLLO.md', label: 'Guia de Desarrollo', icon: Code2 },
    { file: 'HOOKS.md', label: 'Sistema de Hooks', icon: Puzzle },
    { file: 'PERMISOS.md', label: 'Permisos', icon: Lock },
  ];

  const handleOpenDoc = async (doc) => {
    setDocDialog({ open: true, title: doc.label, content: '', loading: true });
    try {
      const response = await pluginsAPI.getDoc(doc.file);
      setDocDialog(prev => ({ ...prev, content: response.data.content, loading: false }));
    } catch {
      toast.error('No se pudo cargar el documento');
      setDocDialog({ open: false, title: '', content: '', loading: false });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#9d8272]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="plugins-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#333333] flex items-center gap-2" data-testid="plugins-title">
            <Package className="w-6 h-6 text-[#9d8272]" />
            Plugins
          </h1>
          <p className="text-sm text-[#888888] mt-1">
            Gestiona extensiones y funcionalidades adicionales de la plataforma
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-[#c5b8a2] text-[#9d8272]"
                data-testid="help-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Ayuda
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {HELP_DOCS.map((doc) => (
                <DropdownMenuItem
                  key={doc.file}
                  onClick={() => handleOpenDoc(doc)}
                  className="cursor-pointer"
                  data-testid={`help-doc-${doc.file}`}
                >
                  <doc.icon className="w-4 h-4 mr-2 text-[#9d8272]" />
                  {doc.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            onClick={handleDownloadDemo}
            disabled={downloadingDemo}
            className="border-[#c5b8a2] text-[#9d8272]"
            data-testid="download-demo-btn"
          >
            {downloadingDemo ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileCode className="w-4 h-4 mr-2" />
            )}
            {downloadingDemo ? 'Descargando...' : 'Plugin Demo'}
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={installing}
            className="bg-[#9d8272] hover:bg-[#8c7161] text-white"
            data-testid="install-plugin-btn"
          >
            {installing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {installing ? 'Instalando...' : 'Instalar Plugin'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={handleInstall}
            data-testid="install-file-input"
          />
        </div>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
              <Input
                placeholder="Buscar por nombre, descripcion o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#c5b8a2]"
                data-testid="plugins-search"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="border-[#c5b8a2]"
              data-testid="plugins-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total" value={plugins.length} icon={Package} color="#9d8272" />
        <StatCard label="Activos" value={plugins.filter(p => p.status === 'active').length} icon={CheckCircle2} color="#22c55e" />
        <StatCard label="Instalados" value={plugins.filter(p => p.status === 'installed' || p.status === 'inactive').length} icon={XCircle} color="#6b7280" />
        <StatCard label="Errores" value={plugins.filter(p => p.status === 'error').length} icon={AlertTriangle} color="#ef4444" />
      </div>

      {/* Lista */}
      <div className="relative">
        {refreshing && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
              <Loader2 className="w-5 h-5 animate-spin text-[#9d8272]" />
              <span className="text-sm text-[#666666]">Actualizando...</span>
            </div>
          </div>
        )}

        {pluginsFiltrados.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Info className="w-12 h-12 text-[#c5b8a2] mx-auto mb-4" />
              <p className="text-[#888888]">
                {searchTerm
                  ? 'No se encontraron plugins con ese filtro'
                  : 'No hay plugins instalados'}
              </p>
              {!searchTerm && (
                <p className="text-xs text-[#aaa] mt-2">
                  Descarga el <strong>Plugin Demo</strong> para crear tu primer plugin
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {pluginsFiltrados.map(plugin => (
              <PluginCard
                key={plugin.plugin_id}
                plugin={plugin}
                expanded={expandedPlugin === plugin.plugin_id}
                onToggle={() => setExpandedPlugin(expandedPlugin === plugin.plugin_id ? null : plugin.plugin_id)}
                onActivate={() => handleActivate(plugin)}
                onDeactivate={() => handleDeactivate(plugin)}
                onUninstall={() => setDeleteDialog({ open: true, plugin })}
                onDownload={() => handleDownload(plugin)}
                isActionLoading={isActionLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dialog de documentacion */}
      <Dialog open={docDialog.open} onOpenChange={(open) => !open && setDocDialog({ open: false, title: '', content: '', loading: false })}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#333]">
              <BookOpen className="w-5 h-5 text-[#9d8272]" />
              {docDialog.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            {docDialog.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#9d8272]" />
              </div>
            ) : (
              <div className="prose prose-sm max-w-none prose-headings:text-[#333] prose-a:text-[#9d8272] prose-code:bg-[#f5f0eb] prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[#8c7161] prose-pre:bg-[#2d2d2d] prose-pre:text-gray-100 prose-th:text-left prose-table:text-sm" data-testid="doc-content">
                <ReactMarkdown>{docDialog.content}</ReactMarkdown>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmacion de desinstalacion */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, plugin: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Desinstalar Plugin
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara el plugin <strong>{deleteDialog.plugin?.name}</strong> y todos sus datos asociados.
              <br /><br />
              <span className="text-xs text-red-500 font-medium">
                Esta accion no se puede deshacer.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#c5b8a2]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUninstall}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-uninstall-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Desinstalar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---- Sub-components ----

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card data-testid={`stat-${label.toLowerCase()}`}>
    <CardContent className="p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <p className="text-lg font-bold text-[#333]">{value}</p>
        <p className="text-xs text-[#888]">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const PluginCard = ({ plugin, expanded, onToggle, onActivate, onDeactivate, onUninstall, onDownload, isActionLoading }) => {
  const statusCfg = STATUS_CONFIG[plugin.status] || STATUS_CONFIG.installed;
  const StatusIcon = statusCfg.icon;
  const permissions = plugin.permissions || [];
  const hooks = plugin.manifest?.hooks || [];

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`plugin-card-${plugin.plugin_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="w-12 h-12 rounded-lg bg-[#f8f6f4] flex items-center justify-center flex-shrink-0">
            {plugin.icon_url ? (
              <img src={plugin.icon_url} alt="" className="w-8 h-8 object-contain" />
            ) : (
              <Package className="w-6 h-6 text-[#9d8272]" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-[#333]">{plugin.name}</h3>
              <Badge variant="outline" className={`text-xs ${statusCfg.color}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusCfg.label}
              </Badge>
              <span className="text-xs text-[#aaa]">v{plugin.version}</span>
            </div>
            <p className="text-sm text-[#888] mt-0.5 line-clamp-1">{plugin.description || 'Sin descripcion'}</p>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-[#aaa]">
              <code className="bg-[#f0ece8] px-2 py-0.5 rounded font-mono text-[#9d8272]">{plugin.plugin_id}</code>
              {plugin.author && <span>por {plugin.author}</span>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {plugin.status === 'active' ? (
              <Button
                variant="ghost" size="sm"
                onClick={onDeactivate}
                disabled={isActionLoading(plugin.plugin_id, 'deactivate')}
                className="text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                title="Desactivar"
                data-testid={`deactivate-${plugin.plugin_id}`}
              >
                {isActionLoading(plugin.plugin_id, 'deactivate') ? <Loader2 className="w-4 h-4 animate-spin" /> : <PowerOff className="w-4 h-4" />}
              </Button>
            ) : (
              <Button
                variant="ghost" size="sm"
                onClick={onActivate}
                disabled={isActionLoading(plugin.plugin_id, 'activate') || plugin.status === 'error'}
                className="text-green-500 hover:text-green-700 hover:bg-green-50"
                title="Activar"
                data-testid={`activate-${plugin.plugin_id}`}
              >
                {isActionLoading(plugin.plugin_id, 'activate') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
              </Button>
            )}
            <Button
              variant="ghost" size="sm"
              onClick={onDownload}
              disabled={isActionLoading(plugin.plugin_id, 'download')}
              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
              title="Descargar ZIP"
              data-testid={`download-${plugin.plugin_id}`}
            >
              {isActionLoading(plugin.plugin_id, 'download') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={onUninstall}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              title="Desinstalar"
              data-testid={`uninstall-${plugin.plugin_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={onToggle}
              className="text-[#888]"
              title="Ver detalles"
              data-testid={`toggle-details-${plugin.plugin_id}`}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-[#e5e0da] grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" data-testid={`details-${plugin.plugin_id}`}>
            {/* Permissions */}
            <div>
              <h4 className="font-medium text-[#555] flex items-center gap-1 mb-2">
                <Shield className="w-3.5 h-3.5" /> Permisos ({permissions.length})
              </h4>
              {permissions.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {permissions.map((p, i) => (
                    <Badge key={i} variant="outline" className="text-xs border-[#c5b8a2] text-[#9d8272]">{p}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#aaa]">Sin permisos especiales</p>
              )}
            </div>
            {/* Hooks */}
            <div>
              <h4 className="font-medium text-[#555] flex items-center gap-1 mb-2">
                <Settings className="w-3.5 h-3.5" /> Hooks ({hooks.length})
              </h4>
              {hooks.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {hooks.map((h, i) => (
                    <Badge key={i} variant="outline" className="text-xs border-[#c5b8a2]">{h.type}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#aaa]">Sin hooks registrados</p>
              )}
            </div>
            {/* Error message */}
            {plugin.error_message && (
              <div className="sm:col-span-2">
                <h4 className="font-medium text-red-600 flex items-center gap-1 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Error
                </h4>
                <p className="text-xs text-red-500 bg-red-50 p-2 rounded">{plugin.error_message}</p>
              </div>
            )}
            {/* Metadata */}
            <div className="sm:col-span-2 text-xs text-[#aaa] flex gap-4">
              {plugin.installed_at && <span>Instalado: {new Date(plugin.installed_at).toLocaleDateString('es-PE')}</span>}
              {plugin.activated_at && <span>Activado: {new Date(plugin.activated_at).toLocaleDateString('es-PE')}</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Plugins;
